import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Play, Info, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeroBanner = ({ movie }) => {
  const navigate = useNavigate();

  if (!movie) return null;

  // Preset overlay label colors
  const labelColors = {
    'NEW': { bg: 'rgba(16, 185, 129, 0.9)', text: '#FFFFFF' },
    'MUST WATCH': { bg: 'rgba(220, 38, 38, 0.9)', text: '#FFFFFF' },
    'FEATURED': { bg: 'rgba(59, 130, 246, 0.9)', text: '#FFFFFF' },
    'TRENDING': { bg: 'rgba(240, 193, 75, 0.95)', text: '#0F171E' },
    'EXCLUSIVE': { bg: 'rgba(139, 92, 246, 0.9)', text: '#FFFFFF' }
  };

  const overlayLabel = movie.overlay_label;
  const overlayDescription = movie.overlay_description;
  const labelStyle = overlayLabel ? (labelColors[overlayLabel.toUpperCase()] || labelColors['FEATURED']) : null;

  return (
    <div className="relative w-full" style={{ height: '80vh' }} data-testid="hero-banner">
      <img
        key={movie.movie_id}
        src={mediaUrl(movie.banner_path, API) || `https://picsum.photos/seed/${movie.movie_id}-banner/1920/1080`}
        alt={movie.title}
        className="w-full h-full object-cover animate-fadeIn"
      />
      <div className="absolute inset-0 hero-gradient"></div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(15, 23, 30, 0.9) 0%, rgba(15, 23, 30, 0.3) 60%, transparent 100%)' }}></div>
      
      <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 md:px-12 lg:px-16 pb-16">
        <div className="max-w-2xl" key={movie.movie_id}>
          {/* Custom overlay label (from admin) OR default Featured badge */}
          {overlayLabel ? (
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-4 animate-fadeIn" style={{
              backgroundColor: labelStyle.bg,
              boxShadow: `0 8px 30px ${labelStyle.bg}, 0 0 60px ${labelStyle.bg}`
            }}>
              <span className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: labelStyle.text }}></span>
              <span className="text-lg sm:text-xl font-black uppercase tracking-widest" style={{ color: labelStyle.text, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                {overlayLabel}
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: 'rgba(59, 130, 246, 0.25)', border: '1px solid #3B82F6' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#3B82F6' }}></span>
              <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#3B82F6' }}>Featured</span>
            </div>
          )}

          {/* Overlay description (admin-set marketing text) - BIG and BOLD */}
          {overlayDescription && (
            <p className="text-2xl sm:text-3xl lg:text-4xl font-black mb-3 animate-fadeIn" style={{
              color: '#F0C14B',
              textShadow: '0 4px 20px rgba(0,0,0,0.8), 0 2px 8px rgba(0,0,0,0.6)',
              letterSpacing: '-0.02em'
            }}>
              {overlayDescription}
            </p>
          )}

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 animate-fadeIn" style={{ color: '#FFFFFF' }}>
            {movie.title}
          </h1>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <span className="px-3 py-1 rounded text-sm font-semibold" style={{ backgroundColor: '#1E3A8A', color: 'white' }}>
              {movie.age_rating}
            </span>
            <span style={{ color: '#DDDDDD' }}>{movie.release_year}</span>
            <span style={{ color: '#DDDDDD' }}>{movie.runtime} min</span>
          </div>
          <p className="text-base sm:text-lg mb-6" style={{ color: '#DDDDDD' }}>
            {movie.synopsis?.substring(0, 200)}...
          </p>
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => navigate(`/movie/${movie.movie_id}`)}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#FFFFFF', color: '#0F171E' }}
              data-testid="hero-view-details-button"
            >
              <Play size={20} fill="#0F171E" />
              Watch Now
            </button>
            <button
              onClick={() => navigate(`/movie/${movie.movie_id}`)}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: 'rgba(109, 121, 132, 0.6)', color: '#FFFFFF', backdropFilter: 'blur(8px)' }}
              data-testid="hero-info-button"
            >
              <Info size={20} />
              More Info
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MovieCard = ({ movie, onClick }) => {
  return (
    <div
      className="movie-card-hover cursor-pointer rounded-lg overflow-hidden flex-shrink-0"
      style={{ width: '200px', backgroundColor: '#1A2632' }}
      onClick={onClick}
      data-testid={`movie-card-${movie.movie_id}`}
    >
      <div className="relative" style={{ aspectRatio: '2/3' }}>
        <img
          src={mediaUrl(movie.poster_path, API) || `https://picsum.photos/seed/${movie.movie_id}/400/600`}
          alt={movie.title}
          className="w-full h-full object-cover"
        />
        {movie.is_restricted && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
            <p className="text-sm text-center px-4 font-semibold" style={{ color: '#EF4444' }}>Age Restricted</p>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-sm mb-1 truncate" style={{ color: '#FFFFFF' }}>{movie.title}</h3>
        <div className="flex items-center justify-between text-xs" style={{ color: '#8197A4' }}>
          <span>{movie.release_year}</span>
          <span className="px-2 py-1 rounded" style={{ backgroundColor: '#0F171E' }}>{movie.age_rating}</span>
        </div>
      </div>
    </div>
  );
};

const Top10Grid = ({ movies, monthLabel, onMovieClick }) => {
  return (
    <div className="mb-10 px-4 sm:px-8 md:px-12 lg:px-16" data-testid="top-10-grid">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <TrendingUp size={24} style={{ color: '#F0C14B' }} />
        <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Top 10 Picks • {monthLabel}</h2>
      </div>
      <div className="grid grid-cols-5 sm:grid-cols-5 md:grid-cols-10 gap-2 sm:gap-3">
        {movies.slice(0, 10).map((movie, idx) => (
          <div
            key={movie.movie_id}
            onClick={() => onMovieClick(movie)}
            className="relative cursor-pointer movie-card-hover rounded-md overflow-hidden"
            style={{ backgroundColor: '#1A2632' }}
            data-testid={`top10-card-${idx + 1}`}
          >
            <div className="relative" style={{ aspectRatio: '2/3' }}>
              <img
                src={mediaUrl(movie.poster_path, API) || `https://picsum.photos/seed/${movie.movie_id}/300/450`}
                alt={movie.title}
                className="w-full h-full object-cover"
              />
              {movie.is_restricted && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}>
                  <p className="text-[10px] text-center px-1 font-semibold" style={{ color: '#EF4444' }}>Restricted</p>
                </div>
              )}
              <div className="absolute top-1 left-1 w-7 h-7 rounded-full flex items-center justify-center text-sm font-black"
                style={{
                  backgroundColor: '#F0C14B',
                  color: '#0F171E',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  border: '2px solid #0F171E'
                }}>
                {idx + 1}
              </div>
            </div>
            <div className="px-1.5 py-2">
              <h3 className="text-xs font-semibold truncate leading-tight" style={{ color: '#FFFFFF' }} title={movie.title}>
                {movie.title}
              </h3>
              <p className="text-[10px] mt-0.5" style={{ color: '#8197A4' }}>{movie.release_year}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MovieRow = ({ title, movies, onMovieClick, icon: Icon, isTop10 }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons);
      return () => el.removeEventListener('scroll', updateScrollButtons);
    }
  }, [movies]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.7;
    scrollRef.current.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <div className="mb-10 group" data-testid={`movie-row-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-4 px-4 sm:px-8 md:px-12 lg:px-16">
        <div className="flex items-center gap-3">
          {Icon && <Icon size={24} style={{ color: isTop10 ? '#F0C14B' : '#3B82F6' }} />}
          <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>{title}</h2>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="p-2 rounded-full transition-all disabled:opacity-30"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' }}
            data-testid={`scroll-left-${title.toLowerCase().replace(/\s+/g, '-')}`}
            aria-label="Scroll left"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="p-2 rounded-full transition-all disabled:opacity-30"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' }}
            data-testid={`scroll-right-${title.toLowerCase().replace(/\s+/g, '-')}`}
            aria-label="Scroll right"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="px-4 sm:px-8 md:px-12 lg:px-16 overflow-x-auto scrollbar-hide scroll-smooth">
        <div className="flex gap-4">
          {movies.map((movie, idx) => (
            <div key={movie.movie_id} className="relative flex-shrink-0">
              {isTop10 && (
                <div className="absolute -left-2 -top-2 z-10 w-12 h-12 rounded-full flex items-center justify-center text-2xl font-black"
                  style={{
                    backgroundColor: '#F0C14B',
                    color: '#0F171E',
                    boxShadow: '0 4px 15px rgba(240, 193, 75, 0.5)',
                    border: '3px solid #0F171E'
                  }}>
                  {idx + 1}
                </div>
              )}
              <MovieCard movie={movie} onClick={() => onMovieClick(movie)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const HomePage = () => {
  const [movies, setMovies] = useState([]);
  const [featuredMovies, setFeaturedMovies] = useState([]);
  const [rotationInterval, setRotationInterval] = useState(7);
  const [watchHistory, setWatchHistory] = useState([]);
  const [topMonthly, setTopMonthly] = useState([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const navigate = useNavigate();

  const fetchTopMonthly = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/movies/top-monthly`, { withCredentials: true });
      setTopMonthly(data.movies || []);
      setMonthLabel(data.month || '');
    } catch (error) {
      console.error('Failed to fetch top-monthly:', error);
    }
  }, []);

  const fetchFeatured = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/movies/featured`, { withCredentials: true });
      setFeaturedMovies(data.movies || []);
      setRotationInterval(data.rotation_interval_seconds || 7);
    } catch (error) {
      console.error('Error fetching featured:', error);
    }
  }, []);

  const fetchMovies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/movies`, { withCredentials: true });
      setMovies(data);
    } catch (error) {
      console.error('Error fetching movies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWatchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/watch-history`, { withCredentials: true });
      setWatchHistory(data);
    } catch (error) {
      console.error('Error fetching watch history:', error);
    }
  }, []);

  useEffect(() => {
    fetchMovies();
    fetchFeatured();
    fetchWatchHistory();
    fetchTopMonthly();
  }, [fetchMovies, fetchFeatured, fetchWatchHistory, fetchTopMonthly]);

  // Auto-rotate featured movie
  useEffect(() => {
    if (featuredMovies.length <= 1) return;

    // Set random initial featured movie
    const randomIndex = Math.floor(Math.random() * featuredMovies.length);
    setFeaturedIndex(randomIndex);

    const interval = setInterval(() => {
      setFeaturedIndex(prev => {
        let next;
        do {
          next = Math.floor(Math.random() * featuredMovies.length);
        } while (next === prev && featuredMovies.length > 1);
        return next;
      });
    }, (rotationInterval || 7) * 1000);

    return () => clearInterval(interval);
  }, [featuredMovies, rotationInterval]);

  const handleMovieClick = (movie) => {
    navigate(`/movie/${movie.movie_id}`);
  };

  const groupMoviesByGenre = () => {
    const genres = {};
    movies.forEach((movie) => {
      movie.genre?.forEach((g) => {
        if (!genres[g]) genres[g] = [];
        genres[g].push(movie);
      });
    });
    return genres;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  const eligibleForFeatured = featuredMovies;
  const featuredMovie = eligibleForFeatured[featuredIndex % Math.max(1, eligibleForFeatured.length)] || movies[0];
  const genreGroups = groupMoviesByGenre();
  const newReleases = [...movies].sort((a, b) => b.release_year - a.release_year).slice(0, 10);
  const recentlyAdded = [...movies].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 10);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0F171E' }}>
      <HeroBanner movie={featuredMovie} />
      
      <div className="py-8">
        {topMonthly.length > 0 && (
          <Top10Grid
            movies={topMonthly}
            monthLabel={monthLabel}
            onMovieClick={handleMovieClick}
          />
        )}

        {watchHistory.length > 0 && (
          <MovieRow
            title="Continue Watching"
            movies={watchHistory.map(h => h.movie).filter(Boolean)}
            onMovieClick={handleMovieClick}
          />
        )}
        
        <MovieRow
          title="New Releases"
          movies={newReleases}
          onMovieClick={handleMovieClick}
        />

        <MovieRow
          title="Recently Added"
          movies={recentlyAdded}
          onMovieClick={handleMovieClick}
        />
        
        {Object.entries(genreGroups).map(([genre, genreMovies]) => (
          <MovieRow
            key={genre}
            title={genre}
            movies={genreMovies.slice(0, 10)}
            onMovieClick={handleMovieClick}
          />
        ))}
      </div>
    </div>
  );
};
